import {
  ACTION_ARGS,
  Arg,
  BUTTON_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  INPUT_MENU_ARGS,
  MENU_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { BUTTON_SIZES, NORMAL } from '../../../../utils/spds/constants/sizes';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { tracked } from '@glimmer/tracking';
import FlexColumn from '../../layout/flex/column.gjs';
import Menu from './dropdown/menu.gjs';
import Super from '../../core/base.gjs';
import Trigger from './dropdown/trigger.gjs';
import closeOnClickOutside from '../../../../modifiers/close-on-click-outside';

const METADATA = Metadata.define({
  description: 'Button with dropdown menu that reveals additional options or actions',
  extends: [
    pick(ACTION_ARGS, ['onClose', 'onOpen']),
    pick(BUTTON_ARGS, ['buttonType', { name: 'label', initialValue: 'Button' }]),
    pick(COMPONENT_STATE_ARGS, ['autofocus', 'disabled']),
    pick(CONTAINER_ARGS, [
      { name: 'fitContent', default: true },
      'fitContentHeight',
      'fitContentWidth',
      'inline',
    ]),
    pick(MENU_ARGS, ['menuItems', 'onItemClick']),
    pick(INPUT_MENU_ARGS, ['noResultsText', 'searchable', 'searchPlaceholder', 'searchThreshold']),
  ],
  meta: {
    size: Arg.string({ default: NORMAL, description: 'Button size', options: BUTTON_SIZES }),
  },
  name: 'Dropdown Button',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=26311-17009&m=dev',
    zeroheight:
      'https://simplepracticedesign.zeroheight.com/styleguide/s/98005/p/87c0a6-dropdown-button',
  },
});

export default class SpdsActionButtonDropdown extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-button spds-action-button';
  variantClass = 'spds-action-button-dropdown';

  @tracked open = false;

  get fitContent() {
    return this.argOrDefault('fitContent');
  }

  get menuId() {
    return this.composeSubcomponentId('menu');
  }

  get trigger() {
    return this.triggerComponent?.baseElement;
  }

  get triggerId() {
    return this.composeSubcomponentId('trigger');
  }

  closeMenu = () => {
    this.open = false;
    this.args.onClose?.();
  };

  toggleOpenState = () => {
    this.open = !this.open;

    this.open ? this.args.onOpen?.() : this.args.onClose?.();
  };

  <template>
    <FlexColumn
      @id={{this.id}}
      class="spds-action-button-dropdown-container"
      data-test-spds-action-button-dropdown
      {{this.setupComponent}}
      {{this.validateArguments}}
      {{closeOnClickOutside this.closeMenu}}
    >
      <Trigger
        @autofocus={{@autofocus}}
        @buttonType={{@buttonType}}
        @disabled={{@disabled}}
        @fitContent={{this.fitContent}}
        @fitContentHeight={{@fitContentHeight}}
        @fitContentWidth={{@fitContentWidth}}
        @id={{this.triggerId}}
        @inline={{@inline}}
        @open={{this.open}}
        @registerComponent={{fn this.registerComponent "triggerComponent"}}
        @size={{@size}}
        @unregisterComponent={{fn this.unregisterComponent "triggerComponent"}}
        aria-controls={{this.menuId}}
        class={{this.classes}}
        {{on "click" this.toggleOpenState}}
        ...attributes
      >
        {{yield to="trigger"}}
        {{@label}}
      </Trigger>
      <Menu
        @anchorElement={{this.trigger}}
        @ariaLabelledBy={{this.triggerId}}
        @id={{this.menuId}}
        @noResultsText={{@noResultsText}}
        @open={{this.open}}
        @searchable={{@searchable}}
        @searchPlaceholder={{@searchPlaceholder}}
        @searchThreshold={{@searchThreshold}}
        @toggleOpenState={{this.toggleOpenState}}
        as |menu|
      >
        {{#if (has-block "menu")}}
          {{yield (hash Item=(component menu.Item onClick=@onItemClick)) to="menu"}}
        {{else if @menuItems}}
          {{#each @menuItems as |item|}}
            <menu.Item
              @onClick={{or item.onClick @onItemClick}}
              @value={{or item.value item.label}}
            >
              {{item.label}}
            </menu.Item>
          {{/each}}
        {{/if}}
      </Menu>
    </FlexColumn>
  </template>
}

export const ACTION_BUTTON_DROPDOWN_METADATA = SpdsActionButtonDropdown.metadata;
